/**
 * Sync-Bereich in den Einstellungen: Server eintragen, Bereich anlegen oder
 * mit Code koppeln, Status, Kopplungscode samt QR-Code, Gerät trennen.
 */

import { renderSVG } from 'uqr';
import * as syncManager from './syncManager.js';
import { decodePairingCode, pairingLink, pairingCodeFromLocation } from './syncConfig.js';

/** Zeitfenster, in dem ein zweiter Klick das Trennen bestätigt */
const CONFIRM_MS = 3000;

const el = (id) => document.getElementById(id);

/**
 * @param {{ showToast: (message: string, type?: string) => void }} deps
 */
export function initSyncPanel({ showToast }) {
  // Vorschlag: Sync-Server unter derselben Domain wie der Reader (siehe sync-server/README.md)
  el('sync-server-url').value = `${location.origin}/sync`;

  el('btn-sync-create').addEventListener('click', (event) =>
    runAction(event.currentTarget, showToast, async () => {
      const code = await syncManager.createSpace(
        el('sync-server-url').value,
        el('sync-token').value
      );
      el('sync-token').value = '';
      showCode(code);
      showToast('Sync-Bereich angelegt. Koppel jetzt deine weiteren Geräte.', 'success');
    })
  );

  el('btn-sync-pair').addEventListener('click', (event) =>
    runAction(event.currentTarget, showToast, async () => {
      await syncManager.pairWithCode(el('sync-code-input').value);
      el('sync-code-input').value = '';
      el('sync-pair-hint').hidden = true;
      showToast('Gerät gekoppelt und abgeglichen.', 'success');
    })
  );

  el('btn-sync-now').addEventListener('click', () => syncManager.syncNow());
  // Der Hinweis zum Kopplungslink passt nicht mehr, sobald ein anderer Code drinsteht
  el('sync-code-input').addEventListener('input', () => (el('sync-pair-hint').hidden = true));

  el('btn-sync-show-code').addEventListener('click', () => {
    const box = el('sync-code-box');
    if (box.hidden) showCode(syncManager.currentPairingCode());
    else box.hidden = true;
  });

  el('btn-sync-copy-code').addEventListener('click', () => copyCode(showToast));
  initUnpairButton(showToast);

  syncManager.onStatus(renderStatus);
  renderStatus(syncManager.getStatus());
  offerPairingFromLink(showToast);
  // Ist der Reader im Tab schon offen, ändert ein Kopplungslink nur das Fragment
  window.addEventListener('hashchange', () => offerPairingFromLink(showToast));
}

/**
 * Führt eine Aktion aus, sperrt dabei den Knopf und meldet Fehler
 */
async function runAction(button, showToast, action) {
  button.disabled = true;
  try {
    await action();
  } catch (error) {
    showToast(error.message, 'error');
  } finally {
    button.disabled = false;
  }
}

/**
 * @param {import('./syncManager.js').SyncStatus} status
 */
function renderStatus(status) {
  el('sync-setup').hidden = status.paired;
  el('sync-paired').hidden = !status.paired;
  if (!status.paired) el('sync-code-box').hidden = true;

  const line = el('sync-status');
  line.classList.toggle('error', Boolean(status.error) && !status.syncing);
  line.textContent = statusText(status);
}

function statusText({ syncing, error, lastSyncAt }) {
  if (syncing) return 'Wird abgeglichen …';
  if (error) return `Abgleich gescheitert: ${error}`;
  if (!lastSyncAt) return 'Noch nicht abgeglichen.';
  const when = new Date(lastSyncAt).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  return `Zuletzt abgeglichen: ${when}`;
}

/**
 * Zeigt den Kopplungscode als Text und als QR-Code mit Kopplungslink
 * @param {string} code
 */
function showCode(code) {
  el('sync-code-output').value = code;
  // renderSVG erzeugt reines SVG aus dem Link – keine fremden Inhalte
  el('sync-qr').innerHTML = renderSVG(pairingLink(code));
  el('sync-code-box').hidden = false;
}

async function copyCode(showToast) {
  const output = el('sync-code-output');
  try {
    await navigator.clipboard.writeText(output.value);
    showToast('Kopplungscode kopiert.', 'success');
  } catch {
    output.select();
    showToast('Bitte den markierten Code von Hand kopieren.', 'info');
  }
}

/**
 * Trennen braucht einen zweiten Klick – danach synchronisiert das Gerät nicht mehr
 */
function initUnpairButton(showToast) {
  const button = el('btn-sync-unpair');
  let confirmTimer = null;
  button.addEventListener('click', () => {
    if (!button.classList.contains('confirm')) {
      button.classList.add('confirm');
      button.textContent = 'Wirklich trennen? Nochmal klicken';
      confirmTimer = setTimeout(() => resetUnpairButton(button), CONFIRM_MS);
      return;
    }
    clearTimeout(confirmTimer);
    resetUnpairButton(button);
    syncManager.unpair();
    showToast('Gerät getrennt. Die Daten bleiben hier und auf dem Server.', 'info');
  });
}

function resetUnpairButton(button) {
  button.classList.remove('confirm');
  button.textContent = 'Gerät trennen';
}

/**
 * Wurde der Reader über einen Kopplungslink geöffnet, wird der Code zum
 * Koppeln vorgeschlagen – aber erst nach einem Klick übernommen. Ein fremder
 * Link soll das Gerät nie unbemerkt an einen fremden Server hängen.
 */
function offerPairingFromLink(showToast) {
  const code = pairingCodeFromLocation();
  if (!code) return;
  history.replaceState(null, '', location.pathname + location.search);

  if (syncManager.getStatus().paired) {
    showToast('Dieses Gerät ist schon gekoppelt. Zum Wechseln erst trennen.', 'info');
    return;
  }
  let serverUrl;
  try {
    serverUrl = decodePairingCode(code).serverUrl;
  } catch (error) {
    showToast(error.message, 'error');
    return;
  }

  el('btn-settings').click();
  el('sync-code-input').value = code;
  const hint = el('sync-pair-hint');
  hint.textContent =
    `Kopplungscode für den Server ${serverUrl}. Mit „Mit Code koppeln“ ` +
    'überträgst du Sitzungen, Einstellungen und API-Schlüssel dorthin.';
  hint.hidden = false;
  el('sync-section').scrollIntoView({ block: 'start' });
}
